import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronLeft, Loader2, Plane, Train, Bus, Ship, Car, Hotel, MapPin, MapPinned,
  Pencil, Receipt, StickyNote, UtensilsCrossed, Ticket, CalendarDays,
  Clock, Layers, Camera,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const KIND_ICON = {
  transport: Plane, stay: Hotel, destination: MapPin,
  reservation: Ticket, activity: Ticket, restaurant: UtensilsCrossed,
  journal: Pencil, expense: Receipt, note: StickyNote,
  checkin: MapPinned,
};

const MODE_ICON = {
  flight: Plane, train: Train, rail: Train, bus: Bus, ferry: Ship, cruise: Ship,
  taxi: Car, car: Car, car_rental: Car, transportation: Car, pickup: Car, hotel: Hotel,
};

const KIND_TINT = {
  transport: "bg-sky-100 text-sky-700 border-sky-200",
  stay: "bg-amber-100 text-amber-700 border-amber-200",
  destination: "bg-emerald-100 text-emerald-700 border-emerald-200",
  reservation: "bg-indigo-100 text-indigo-700 border-indigo-200",
  activity: "bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200",
  restaurant: "bg-orange-100 text-orange-700 border-orange-200",
  journal: "bg-rose-100 text-rose-700 border-rose-200",
  expense: "bg-slate-100 text-slate-700 border-slate-200",
  note: "bg-zinc-100 text-zinc-700 border-zinc-200",
  checkin: "bg-rose-100 text-rose-700 border-rose-200",
};

/** TripJourney — Phase 3 chronological view of planned + actual events for a trip.
    Pulls from `/api/trips/:id/journey/events` which reuses the canonical Trip ID. */
export default function TripJourney() {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [layer, setLayer] = useState("all"); // all | planned | actual

  useEffect(() => {
    if (authLoading || !user) return;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/trips/${id}/journey/events`);
        setData(r.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not load journey");
      } finally { setLoading(false); }
    })();
  }, [id, user, authLoading]);

  const grouped = useMemo(() => {
    if (!data) return [];
    const evs = (data.events || []).filter((e) => layer === "all" || e.layer === layer);
    const byDay = {};
    evs.forEach((e) => {
      const d = e.date || "undated";
      (byDay[d] = byDay[d] || []).push(e);
    });
    return Object.entries(byDay).sort(([a], [b]) => a.localeCompare(b));
  }, [data, layer]);

  if (loading) {
    return <div className="min-h-screen grid place-items-center bg-slate-50">
      <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
    </div>;
  }
  if (!data) return null;

  const t = data.trip || {};
  const stats = data.stats || {};
  const dateFmt = (d) => d === "undated" ? "No date set" :
    new Date(d).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });

  return (
    <div className="min-h-screen bg-slate-50 pb-20" data-testid="trip-journey">
      <div className="max-w-3xl mx-auto px-4 pt-5">
        <Link to={`/trips/${id}`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-emerald-700 mb-3" data-testid="journey-back">
          <ChevronLeft className="w-4 h-4" /> Back to trip
        </Link>

        <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4 shadow-sm">
          <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-emerald-700 mb-1">Journey timeline</div>
          <h1 className="font-serif font-900 text-3xl text-slate-900 flex items-center gap-2">
            <Layers className="w-6 h-6 text-emerald-600" /> {t.title || "Trip Journey"}
          </h1>
          <div className="text-sm text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
            {t.start_date && <span className="inline-flex items-center gap-1"><CalendarDays className="w-4 h-4" /> {t.start_date}{t.end_date && ` → ${t.end_date}`}</span>}
            <span>·</span>
            <span data-testid="journey-stats-total">{stats.total} events</span>
            <span className="text-emerald-700" data-testid="journey-stats-planned">{stats.planned} planned</span>
            <span className="text-rose-700" data-testid="journey-stats-actual">{stats.actual} actual</span>
          </div>
          {(data.my_role === "owner" || data.my_role === "edit") && (
            <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="journey-actions">
              <CheckInButton tripId={id} onCheckedIn={load} />
              {data.my_role === "owner" && <FollowShareDialog tripId={id} />}
            </div>
          )}
        </div>

        <div className="flex gap-1 mb-4 overflow-x-auto scrollbar-none">
          {[["all", "All"], ["planned", "Planned"], ["actual", "Actual"]].map(([k, l]) => (
            <button key={k} onClick={() => setLayer(k)} data-testid={`journey-filter-${k}`}
              className={`px-3 py-1.5 text-xs font-700 rounded-full border transition-colors ${layer === k ? "bg-emerald-600 text-white border-emerald-600" : "bg-white border-slate-300 text-slate-700 hover:border-emerald-400"}`}>
              {l}
            </button>
          ))}
        </div>

        {grouped.length === 0 ? (
          <div className="border border-dashed border-slate-300 rounded-lg p-8 text-center" data-testid="journey-empty">
            <Layers className="w-8 h-8 mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-500">No events yet. Add transport, stays or a journal entry on your trip page and they'll appear here.</p>
          </div>
        ) : grouped.map(([day, items]) => (
          <section key={day} className="mb-5" data-testid={`journey-day-${day}`}>
            <h2 className="font-heading font-800 text-sm text-slate-600 uppercase tracking-wider mb-2 inline-flex items-center gap-2">
              <CalendarDays className="w-4 h-4" /> {dateFmt(day)}
            </h2>
            <div className="relative pl-5 before:absolute before:left-1.5 before:top-2 before:bottom-2 before:w-px before:bg-slate-200">
              {items.map((e) => <EventRow key={e.id} e={e} />)}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function EventRow({ e }) {
  const Icon = (e.mode && MODE_ICON[e.mode]) || KIND_ICON[e.kind] || StickyNote;
  const tint = KIND_TINT[e.kind] || KIND_TINT.note;
  return (
    <div className="relative mb-3" data-testid={`journey-event-${e.id}`}>
      <span className={`absolute -left-[18px] top-2.5 w-3 h-3 rounded-full border-2 ${e.layer === "actual" ? "bg-rose-500 border-rose-200" : "bg-emerald-500 border-emerald-200"}`} />
      <div className={`border rounded-lg p-3 bg-white hover:shadow-sm transition-shadow ${tint.split(" ").filter(c => c.startsWith("border-")).join(" ") || "border-slate-200"}`}>
        <div className="flex items-start gap-3">
          <div className={`shrink-0 w-9 h-9 rounded-lg grid place-items-center border ${tint}`}>
            <Icon className="w-4 h-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-700 text-slate-900 truncate">{e.title}</span>
              <span className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded ${e.layer === "actual" ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`}>
                {e.layer}
              </span>
            </div>
            {e.subtitle && <div className="text-xs text-slate-500 mt-0.5">{e.subtitle}</div>}
            <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-2 flex-wrap">
              {e.time && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {e.time}</span>}
              {typeof e.photos_count === "number" && e.photos_count > 0 && <span className="inline-flex items-center gap-1"><Camera className="w-3 h-3" /> {e.photos_count}</span>}
              <span className="font-mono">{e.kind}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
